'use client';

import { Box, HStack, VStack, token } from '@terra/ui-styled-system';
import {
  Text,
  Badge,
  IconButton,
  Tooltip,
  HttpEditor,
  Splitter,
  Switch,
  Button,
  ConfirmationDialog,
  Table,
} from '@terra/ui-design-system';
import type {
  CellStyle,
  ColDef,
  ICellRendererParams,
  RowClickedEvent,
} from 'ag-grid-community';
import {
  ArrowDown02Icon,
  ArrowUp02Icon,
  Copy01Icon,
  Download01Icon,
  Edit01Icon,
  Add01Icon,
  ReplaceIcon,
  Delete02Icon,
  Upload01Icon,
} from '@terra/ui-icons';
import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { useTranslations } from 'next-intl';
import { applicationsClient } from '@terra/shared-rest-clients-applications';
import { useProxyStore, type MatchReplaceRule } from '../../store/proxy-store';
import { useLayoutStore } from '../../store/layoutStore';
import { TARGET_TYPE } from '../../utils/target-scope.utils';
import {
  useProjectStore,
  useTargetId,
  useTargetType,
} from '../../store/projectStore';
import { getAuthHeaders } from '../../utils/authHeaders';
import { getCurrentGatewayUrl } from '../../utils/environmentDetection';
import { useMatchReplacePersistence } from '../../hooks/useMatchReplacePersistence';
import { useResolvedRecentHistoryRequests } from '../../hooks/useResolvedRecentHistoryRequests';
import { useResolvedBodies } from '../../hooks/useResolvedBodies';
import { EmptyState } from '../EmptyState';
import { toasterApi } from '../../app/providers';
import {
  formatHttpRequestRaw,
  formatHttpResponseRaw,
} from '../../utils/formatHttpForPanel';
import { filterRequestsForMatchReplaceRule } from '../../utils/match-replace-conditions';
import {
  applyMatchReplaceRulePreview,
  buildRuleMatchCounts,
  countRuleMatchesInHistory,
} from '../../utils/match-replace-preview';
import {
  getProxyModifiedIndicatorColor,
  getProxyModifiedRowBackground,
} from '../../utils/proxy-modified-styles';
import { resolveEffectiveHttpVersion } from '@terra/shared-utils';
import { InlineRuleEditor } from './InlineRuleEditor';
import {
  buildRuleFromForm,
  createDefaultRuleForm,
  formatRuleOrigins,
  mapRuleToForm,
} from './match-replace-rule-form';
import { BorderedSplitterResizeTrigger } from '../layout/BorderedSplitterResizeTrigger';
import { usePersistedSplitterSizes } from '../../hooks/layout/usePersistedSplitterSizes';
import { useMatchReplaceRulesSelection } from './match-replace/useMatchReplaceRulesSelection';
import { MatchReplaceRulesBulkActionsBar } from './match-replace/MatchReplaceRulesBulkActionsBar';
import {
  MatchReplaceSelectAllHeader,
  MatchReplaceSelectionCell,
} from './match-replace/MatchReplaceSelectionCells';

const RULES_ROW_HEIGHT = 36;
const toSplitterPanels = (
  panels: Array<{
    id: string | number;
    minSize?: number;
  }>,
) =>
  panels.map(({ id, minSize }) => ({
    id: id.toString(),
    minSize,
  }));
const rulesCellStyle = {
  display: 'flex',
  alignItems: 'center',
} as CellStyle;

function RulesTableCell({ children }: { children: React.ReactNode }) {
  return (
    <HStack alignItems="center" h="full" minW={0} w="full">
      {children}
    </HStack>
  );
}

export function SimpleMatchReplaceTab() {
  const {
    settings,
    addMatchReplaceRule,
    updateMatchReplaceRule,
    removeMatchReplaceRule,
    removeMatchReplaceRules,
    setMatchReplaceRulesEnabled,
    duplicateMatchReplaceRule,
    reorderMatchReplaceRules,
    exportMatchReplaceRules,
    importMatchReplaceRules,
    replaceMatchReplaceRulesForApplication,
    matchReplaceDirtyRuleIds,
    matchReplacePersistStatus,
    matchReplacePersistError,
    matchReplaceRuleStats,
    matchReplaceFocusRuleId,
    setMatchReplaceFocusRuleId,
  } = useProxyStore();
  const selectedTarget = useProjectStore((state) => state.selectedTarget);
  const targetId = useTargetId();
  const targetType = useTargetType();
  const currentTargetId = useProxyStore((state) => state.currentTargetId);
  const resolvedTargetId = targetId || currentTargetId || 'standalone';
  const resolvedTargetType =
    selectedTarget?.targetType ?? targetType ?? TARGET_TYPE.WEB_APPLICATION;
  const requests = useResolvedRecentHistoryRequests({
    targetId: resolvedTargetId,
    targetType: resolvedTargetType,
    limit: 50,
  });
  const matchReplaceRequestsPreviewSplitterSizes = useLayoutStore(
    (state) => state.matchReplaceRequestsPreviewSplitterSizes,
  );
  const setMatchReplaceRequestsPreviewSplitterSizes = useLayoutStore(
    (state) => state.setMatchReplaceRequestsPreviewSplitterSizes,
  );
  const matchReplaceOriginalTransformedSplitterSizes = useLayoutStore(
    (state) => state.matchReplaceOriginalTransformedSplitterSizes,
  );
  const setMatchReplaceOriginalTransformedSplitterSizes = useLayoutStore(
    (state) => state.setMatchReplaceOriginalTransformedSplitterSizes,
  );
  const matchReplaceRulesEditorSplitterSizes = useLayoutStore(
    (state) => state.matchReplaceRulesEditorSplitterSizes,
  );
  const setMatchReplaceRulesEditorSplitterSizes = useLayoutStore(
    (state) => state.setMatchReplaceRulesEditorSplitterSizes,
  );
  const matchReplaceRulesPreviewSplitterSizes = useLayoutStore(
    (state) => state.matchReplaceRulesPreviewSplitterSizes,
  );
  const setMatchReplaceRulesPreviewSplitterSizes = useLayoutStore(
    (state) => state.setMatchReplaceRulesPreviewSplitterSizes,
  );
  const rulesPreviewLayout = usePersistedSplitterSizes({
    panels: matchReplaceRulesPreviewSplitterSizes,
    setPanels: setMatchReplaceRulesPreviewSplitterSizes,
  });
  const requestsPreviewLayout = usePersistedSplitterSizes({
    panels: matchReplaceRequestsPreviewSplitterSizes,
    setPanels: setMatchReplaceRequestsPreviewSplitterSizes,
  });
  const originalTransformedLayout = usePersistedSplitterSizes({
    panels: matchReplaceOriginalTransformedSplitterSizes,
    setPanels: setMatchReplaceOriginalTransformedSplitterSizes,
  });
  const rulesEditorLayout = usePersistedSplitterSizes({
    panels: matchReplaceRulesEditorSplitterSizes,
    setPanels: setMatchReplaceRulesEditorSplitterSizes,
  });
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(
    null,
  );
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  const [isRuleEditorOpen, setIsRuleEditorOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<MatchReplaceRule | null>(null);
  const [isClearingGlobalRules, setIsClearingGlobalRules] = useState(false);
  const [isClearGlobalDialogOpen, setIsClearGlobalDialogOpen] = useState(false);
  const updateSelectedTarget = useProjectStore((s) => s.updateSelectedTarget);
  const tRuleDialog = useTranslations('matchReplace.ruleDialog');
  const tGlobalRules = useTranslations('matchReplace.globalRules');
  const tPreview = useTranslations('matchReplace.preview');
  const tBulk = useTranslations('matchReplace.bulkActions');
  const tCommon = useTranslations('common');
  const [ruleForm, setRuleForm] = useState(createDefaultRuleForm);
  const matchReplaceRules = React.useMemo(
    () => settings.matchReplaceRules || [],
    [settings.matchReplaceRules],
  );
  const {
    selectedRuleIds,
    selectedCount,
    headerCheckboxState,
    isRuleSelected,
    toggleRuleSelected,
    selectAll,
    clearSelection,
  } = useMatchReplaceRulesSelection({ rules: matchReplaceRules });
  const serverBrowserConfig = selectedTarget?.browserConfig;
  const globalRulesHydrationGenerationRef = useRef(0);
  const { schedulePersistRulesToServer, persistRulesToServerNow } =
    useMatchReplacePersistence({
      onPersistError: () => {
        toasterApi.error({
          title: tGlobalRules('saveErrorTitle'),
          description: tGlobalRules('saveErrorDescription'),
        });
      },
    });
  const hasUnsavedRules =
    matchReplaceDirtyRuleIds.length > 0 ||
    matchReplacePersistStatus === 'error';

  useEffect(() => {
    if (!selectedTarget?.id) {
      return;
    }

    if (serverBrowserConfig !== undefined) {
      return;
    }

    const applicationId = selectedTarget.id;
    const generation = ++globalRulesHydrationGenerationRef.current;

    void (async () => {
      try {
        const authHeaders = await getAuthHeaders();
        const getRes = await applicationsClient.getApplication({
          params: { id: applicationId },
          overrideClientOptions: {
            baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
          },
          extraHeaders: authHeaders,
        });

        if (generation !== globalRulesHydrationGenerationRef.current) {
          return;
        }

        if (getRes.status !== 200) {
          throw new Error('Failed to load application');
        }

        const currentApp = useProjectStore.getState().selectedTarget;

        if (!currentApp || currentApp.id !== applicationId) {
          return;
        }

        const fresh = getRes.body;

        updateSelectedTarget({
          ...currentApp,
          ...fresh,
          browserConfig: fresh.browserConfig ?? null,
        });
      } catch {
        if (generation === globalRulesHydrationGenerationRef.current) {
          toasterApi.error({
            title: tGlobalRules('loadGlobalRulesErrorTitle'),
            description: tGlobalRules('loadGlobalRulesErrorDescription'),
          });
        }
      }
    })();
  }, [
    selectedTarget?.id,
    serverBrowserConfig,
    tGlobalRules,
    updateSelectedTarget,
  ]);
  const persistRulesToServer = schedulePersistRulesToServer;

  React.useEffect(() => {
    if (matchReplaceRules.length > 0 && !selectedRuleId) {
      setSelectedRuleId(matchReplaceRules[0].id);
    } else if (matchReplaceRules.length === 0) {
      setSelectedRuleId(null);
    } else if (
      selectedRuleId &&
      !matchReplaceRules.find((rule) => rule.id === selectedRuleId)
    ) {
      setSelectedRuleId(matchReplaceRules[0]?.id || null);
    }
  }, [matchReplaceRules, selectedRuleId]);
  React.useEffect(() => {
    if (!matchReplaceFocusRuleId) {
      return;
    }

    if (matchReplaceRules.some((rule) => rule.id === matchReplaceFocusRuleId)) {
      setSelectedRuleId(matchReplaceFocusRuleId);
      setMatchReplaceFocusRuleId(null);
    }
  }, [matchReplaceFocusRuleId, matchReplaceRules, setMatchReplaceFocusRuleId]);
  const previewRule = useMemo(() => {
    if (isRuleEditorOpen) {
      return buildRuleFromForm({ form: ruleForm, editingRule });
    }

    return matchReplaceRules.find((rule) => rule.id === selectedRuleId) ?? null;
  }, [
    isRuleEditorOpen,
    ruleForm,
    editingRule,
    matchReplaceRules,
    selectedRuleId,
  ]);
  const previewRequests = useMemo(
    () =>
      filterRequestsForMatchReplaceRule({
        requests,
        rule: previewRule,
      }),
    [requests, previewRule],
  );
  const baseRuleMatchCounts = useMemo(
    () =>
      buildRuleMatchCounts({
        rules: matchReplaceRules,
        stats: matchReplaceRuleStats,
        requests,
      }),
    [matchReplaceRules, matchReplaceRuleStats, requests],
  );
  const editingRuleLiveMatchCount = useMemo(() => {
    if (!isRuleEditorOpen || !previewRule || !editingRule?.id) {
      return null;
    }

    return countRuleMatchesInHistory({
      rule: previewRule,
      requests,
    });
  }, [isRuleEditorOpen, previewRule, editingRule?.id, requests]);
  const ruleMatchCounts = useMemo(() => {
    if (editingRule?.id == null || editingRuleLiveMatchCount === null) {
      return baseRuleMatchCounts;
    }

    return {
      ...baseRuleMatchCounts,
      [editingRule.id]: editingRuleLiveMatchCount,
    };
  }, [baseRuleMatchCounts, editingRule?.id, editingRuleLiveMatchCount]);

  useEffect(() => {
    if (previewRequests.length === 0) {
      setSelectedRequestId(null);

      return;
    }

    if (
      selectedRequestId &&
      previewRequests.some((request) => request.id === selectedRequestId)
    ) {
      return;
    }

    setSelectedRequestId(previewRequests[0]?.id ?? null);
  }, [previewRequests, selectedRequestId]);
  const selectedRequest = previewRequests.find(
    (req) => req.id === selectedRequestId,
  );
  const resolvedBodies = useResolvedBodies(selectedRequest);
  const selectedRequestForPreview = useMemo(() => {
    if (!selectedRequest) {
      return null;
    }

    return {
      ...selectedRequest,
      body: resolvedBodies.requestBody,
      responseBody: resolvedBodies.responseBody,
    };
  }, [selectedRequest, resolvedBodies]);
  const formattedRequest = useMemo(() => {
    if (!selectedRequestForPreview) return '';
    const httpVersion = resolveEffectiveHttpVersion({
      httpVersion: selectedRequestForPreview.httpVersion,
      headers:
        selectedRequestForPreview.rawHeaders ??
        selectedRequestForPreview.headers,
    });

    return formatHttpRequestRaw({
      method: selectedRequestForPreview.method,
      url: selectedRequestForPreview.url,
      headers: selectedRequestForPreview.headers,
      rawHeaders: selectedRequestForPreview.rawHeaders,
      body: selectedRequestForPreview.body || undefined,
      httpVersion,
    });
  }, [selectedRequestForPreview]);
  const formattedResponse = useMemo(() => {
    if (!selectedRequestForPreview?.status) return '';
    const httpVersion = resolveEffectiveHttpVersion({
      httpVersion: selectedRequestForPreview.responseHttpVersion,
      headers:
        selectedRequestForPreview.responseRawHeaders ??
        selectedRequestForPreview.responseHeaders,
    });

    return formatHttpResponseRaw({
      status: selectedRequestForPreview.status,
      statusMessage: selectedRequestForPreview.responseStatusMessage ?? '',
      headers: selectedRequestForPreview.responseHeaders ?? {},
      rawHeaders: selectedRequestForPreview.responseRawHeaders,
      body: selectedRequestForPreview.responseBody || undefined,
      httpVersion,
    });
  }, [selectedRequestForPreview]);
  const previewResult = useMemo(() => {
    if (!previewRule || !selectedRequestForPreview) {
      return null;
    }

    return applyMatchReplaceRulePreview({
      rule: previewRule,
      request: selectedRequestForPreview,
    });
  }, [previewRule, selectedRequestForPreview]);
  const resetRuleForm = useCallback(() => {
    setRuleForm(createDefaultRuleForm());
  }, []);
  const handleOpenCreateRule = useCallback(() => {
    resetRuleForm();
    setEditingRule(null);
    setIsRuleEditorOpen(true);
  }, [resetRuleForm]);
  const handleCloseRuleEditor = useCallback(() => {
    setIsRuleEditorOpen(false);
    setEditingRule(null);
    resetRuleForm();
  }, [resetRuleForm]);
  const handleAddRule = useCallback(() => {
    if (!ruleForm.matchPattern.trim()) {
      return;
    }

    const rule = buildRuleFromForm({ form: ruleForm, editingRule });

    if (editingRule) {
      updateMatchReplaceRule(editingRule.id, { ...rule, id: editingRule.id });
      setSelectedRuleId(editingRule.id);
      setEditingRule(null);
    } else {
      addMatchReplaceRule(rule);
      setSelectedRuleId(rule.id);
    }

    resetRuleForm();
    setIsRuleEditorOpen(false);
    void persistRulesToServer();
  }, [
    ruleForm,
    editingRule,
    addMatchReplaceRule,
    updateMatchReplaceRule,
    resetRuleForm,
    persistRulesToServer,
  ]);
  const handleEditRule = useCallback((rule: MatchReplaceRule) => {
    setEditingRule(rule);
    setRuleForm(mapRuleToForm(rule));
    setSelectedRuleId(rule.id);
    setIsRuleEditorOpen(true);
  }, []);
  const handleRemoveRule = useCallback(
    (ruleId: string) => {
      removeMatchReplaceRule(ruleId);
      void persistRulesToServer();
    },
    [removeMatchReplaceRule, persistRulesToServer],
  );
  const handleDuplicateRule = useCallback(
    (ruleId: string) => {
      duplicateMatchReplaceRule(ruleId);
      void persistRulesToServer();
      toasterApi.success({
        title: 'Rule Duplicated',
        description: 'Rule has been duplicated successfully',
      });
    },
    [duplicateMatchReplaceRule, persistRulesToServer],
  );
  const handleMoveRuleUp = useCallback(
    (index: number) => {
      if (index > 0) {
        reorderMatchReplaceRules(index, index - 1);
        void persistRulesToServer();
      }
    },
    [reorderMatchReplaceRules, persistRulesToServer],
  );
  const handleMoveRuleDown = useCallback(
    (index: number) => {
      if (index < matchReplaceRules.length - 1) {
        reorderMatchReplaceRules(index, index + 1);
        void persistRulesToServer();
      }
    },
    [reorderMatchReplaceRules, matchReplaceRules.length, persistRulesToServer],
  );
  const rulesDefaultColDef = useMemo(
    () => ({
      sortable: false,
      filter: false,
      resizable: true,
      cellStyle: rulesCellStyle,
      suppressSizeToFit: true,
    }),
    [],
  );
  const getRuleRowId = useCallback(
    (params: { data: MatchReplaceRule }) => params.data.id,
    [],
  );
  const getRuleRowStyle = useCallback(
    (params: { data?: MatchReplaceRule }): CellStyle => {
      if (params.data && params.data.id === selectedRuleId) {
        return { cursor: 'pointer', background: token('colors.bg.muted') };
      }

      return { cursor: 'pointer' };
    },
    [selectedRuleId],
  );
  const handleRuleRowClicked = useCallback(
    (event: RowClickedEvent<MatchReplaceRule>) => {
      if (event.data) {
        setSelectedRuleId(event.data.id);
      }
    },
    [],
  );
  const rulesColumnDefs: ColDef<MatchReplaceRule>[] = useMemo(
    () => [
      {
        colId: 'bulkSelect',
        headerName: '',
        width: 40,
        minWidth: 40,
        maxWidth: 40,
        pinned: 'left',
        lockPosition: true,
        suppressMovable: true,
        resizable: false,
        suppressNavigable: true,
        headerComponent: () => (
          <MatchReplaceSelectAllHeader
            checked={
              matchReplaceRules.length > 0 &&
              selectedCount === matchReplaceRules.length
                ? true
                : headerCheckboxState
            }
            disabled={matchReplaceRules.length === 0}
            onCheckedChange={(selected) =>
              selectAll({ rules: matchReplaceRules, selected })
            }
          />
        ),
        cellRenderer: (params: ICellRendererParams<MatchReplaceRule>) =>
          params.data ? (
            <MatchReplaceSelectionCell
              selected={isRuleSelected(params.data.id)}
              onCheckedChange={(selected) => {
                if (params.data) {
                  toggleRuleSelected({ ruleId: params.data.id, selected });
                }
              }}
            />
          ) : null,
        cellStyle: {
          padding: 0,
        } as CellStyle,
      },
      {
        colId: 'enabled',
        field: 'enabled',
        headerName: '',
        width: 52,
        minWidth: 52,
        maxWidth: 52,
        pinned: 'left',
        lockPosition: true,
        suppressMovable: true,
        resizable: false,
        suppressNavigable: true,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        } as CellStyle,
        cellRenderer: (
          params: ICellRendererParams<MatchReplaceRule, boolean>,
        ) => (
          <HStack alignItems="center" justifyContent="center" h="full" w="full">
            <Switch
              size="sm"
              checked={params.value ?? false}
              onCheckedChange={(details) => {
                if (params.data) {
                  updateMatchReplaceRule(params.data.id, {
                    enabled: details.checked,
                  });
                  void persistRulesToServer();
                }
              }}
            />
          </HStack>
        ),
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 100,
        cellRenderer: (
          params: ICellRendererParams<MatchReplaceRule, string>,
        ) => (
          <RulesTableCell>
            <Text
              fontSize="xs"
              fontWeight="medium"
              flex="1"
              minW={0}
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              title={params.value ?? undefined}
            >
              {params.value}
            </Text>
          </RulesTableCell>
        ),
      },
      {
        field: 'matchPattern',
        headerName: 'Pattern',
        flex: 1,
        minWidth: 80,
        cellRenderer: (
          params: ICellRendererParams<MatchReplaceRule, string>,
        ) => (
          <RulesTableCell>
            <Text
              fontSize="xs"
              flex="1"
              minW={0}
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              title={params.value ?? undefined}
            >
              {params.value}
            </Text>
          </RulesTableCell>
        ),
      },
      {
        field: 'matchType',
        headerName: 'Type',
        width: 96,
        minWidth: 96,
        cellRenderer: (
          params: ICellRendererParams<MatchReplaceRule, string>,
        ) => (
          <RulesTableCell>
            <Badge
              size="sm"
              variant="emphasis"
              maxW="full"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              title={params.value ?? undefined}
            >
              {params.value}
            </Badge>
          </RulesTableCell>
        ),
      },
      {
        colId: 'applyTo',
        headerName: tRuleDialog('scope'),
        width: 170,
        minWidth: 120,
        valueGetter: (params) => formatRuleOrigins(params.data, tRuleDialog),
        cellRenderer: (
          params: ICellRendererParams<MatchReplaceRule, string>,
        ) => (
          <RulesTableCell>
            <Badge
              variant="outline"
              size="sm"
              maxW="full"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              title={params.value ?? undefined}
            >
              {params.value}
            </Badge>
          </RulesTableCell>
        ),
      },
      {
        colId: 'matchCount',
        headerName: 'Matches',
        width: 88,
        minWidth: 88,
        valueGetter: (params) => ruleMatchCounts[params.data?.id ?? ''] ?? 0,
        cellRenderer: (
          params: ICellRendererParams<MatchReplaceRule, number>,
        ) => (
          <RulesTableCell>
            <Text fontSize="xs">{params.value ?? 0}</Text>
          </RulesTableCell>
        ),
      },
      {
        colId: 'actions',
        headerName: 'Actions',
        initialWidth: 150,
        minWidth: 150,
        resizable: false,
        sortable: false,
        filter: false,
        suppressNavigable: true,
        cellRenderer: (params: ICellRendererParams<MatchReplaceRule>) => {
          const index = params.node?.rowIndex ?? 0;
          const rule = params.data;

          if (!rule) {
            return null;
          }

          return (
            <HStack gap="0" alignItems="center" h="full" w="full">
              <Tooltip title="Move up">
                <IconButton
                  aria-label="Move up"
                  size="xs"
                  variant="ghost"
                  onClick={() => handleMoveRuleUp(index)}
                  disabled={index === 0}
                >
                  <ArrowUp02Icon size={10} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Move down">
                <IconButton
                  aria-label="Move down"
                  size="xs"
                  variant="ghost"
                  onClick={() => handleMoveRuleDown(index)}
                  disabled={index === matchReplaceRules.length - 1}
                >
                  <ArrowDown02Icon size={10} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit rule">
                <IconButton
                  aria-label="Edit rule"
                  size="xs"
                  variant="ghost"
                  onClick={() => handleEditRule(rule)}
                >
                  <Edit01Icon size={10} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Duplicate rule">
                <IconButton
                  aria-label="Duplicate rule"
                  size="xs"
                  variant="ghost"
                  onClick={() => handleDuplicateRule(rule.id)}
                >
                  <Copy01Icon size={10} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete rule">
                <IconButton
                  aria-label="Delete rule"
                  size="xs"
                  variant="ghost"
                  onClick={() => handleRemoveRule(rule.id)}
                >
                  <Delete02Icon size={10} />
                </IconButton>
              </Tooltip>
            </HStack>
          );
        },
      },
    ],
    [
      handleDuplicateRule,
      handleEditRule,
      handleMoveRuleDown,
      handleMoveRuleUp,
      handleRemoveRule,
      ruleMatchCounts,
      tRuleDialog,
      persistRulesToServer,
      updateMatchReplaceRule,
      isRuleSelected,
      toggleRuleSelected,
      selectAll,
      selectedCount,
      headerCheckboxState,
      matchReplaceRules,
    ],
  );
  const handleExport = useCallback(() => {
    try {
      const rulesJson = exportMatchReplaceRules();
      const blob = new Blob([rulesJson], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = 'match-replace-rules.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toasterApi.success({
        title: 'Rules Exported',
        description: 'Rules exported successfully',
      });
    } catch {
      toasterApi.error({
        title: 'Export Failed',
        description: 'Failed to export rules',
      });
    }
  }, [exportMatchReplaceRules]);
  const handleImport = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      if (!file) return;
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;

          importMatchReplaceRules(content);
          void persistRulesToServer();
          toasterApi.success({
            title: 'Rules Imported',
            description: 'Rules imported successfully',
          });
        } catch {
          toasterApi.error({
            title: 'Import Failed',
            description: 'Invalid rules file',
          });
        }
      };

      reader.readAsText(file);
      event.target.value = '';
    },
    [importMatchReplaceRules, persistRulesToServer],
  );
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  const handleBulkEnableSelected = useCallback(() => {
    const ids = [...selectedRuleIds];

    setMatchReplaceRulesEnabled({ ruleIds: ids, enabled: true });
    void persistRulesToServer();
  }, [selectedRuleIds, setMatchReplaceRulesEnabled, persistRulesToServer]);
  const handleBulkDisableSelected = useCallback(() => {
    const ids = [...selectedRuleIds];

    setMatchReplaceRulesEnabled({ ruleIds: ids, enabled: false });
    void persistRulesToServer();
  }, [selectedRuleIds, setMatchReplaceRulesEnabled, persistRulesToServer]);
  const handleBulkExportSelected = useCallback(() => {
    try {
      const rulesJson = exportMatchReplaceRules([...selectedRuleIds]);
      const blob = new Blob([rulesJson], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = 'match-replace-rules.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toasterApi.success({
        title: tGlobalRules('exportRules'),
      });
    } catch {
      toasterApi.error({
        title: tGlobalRules('exportRules'),
      });
    }
  }, [selectedRuleIds, exportMatchReplaceRules, tGlobalRules]);
  const handleBulkDeleteSelected = useCallback(() => {
    removeMatchReplaceRules([...selectedRuleIds]);
    void persistRulesToServer();
    clearSelection();
    setIsBulkDeleteDialogOpen(false);
  }, [
    selectedRuleIds,
    removeMatchReplaceRules,
    persistRulesToServer,
    clearSelection,
  ]);
  const handleConfirmClearGlobalRules = useCallback(async () => {
    if (!selectedTarget?.id) {
      return;
    }

    setIsClearingGlobalRules(true);

    try {
      const authHeaders = await getAuthHeaders();
      const getRes = await applicationsClient.getApplication({
        params: { id: selectedTarget.id },
        overrideClientOptions: {
          baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
        },
        extraHeaders: authHeaders,
      });

      if (getRes.status !== 200) {
        throw new Error('Failed to load application');
      }

      const app = getRes.body;
      const prevHttp = app.browserConfig?.httpInterceptionRules ?? [];
      const updateRes = await applicationsClient.updateApplication({
        params: { id: selectedTarget.id },
        body: {
          browserConfig: {
            httpInterceptionRules: prevHttp,
            matchReplaceRules: [],
          },
        },
        overrideClientOptions: {
          baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
        },
        extraHeaders: authHeaders,
      });

      if (updateRes.status !== 200) {
        throw new Error('Failed to update application');
      }

      updateSelectedTarget({
        ...selectedTarget,
        browserConfig: {
          httpInterceptionRules: prevHttp,
          matchReplaceRules: [],
        },
      });
      replaceMatchReplaceRulesForApplication([]);
      setIsClearGlobalDialogOpen(false);
      toasterApi.success({
        title: tGlobalRules('clearSuccessTitle'),
        description: tGlobalRules('clearSuccessDescription'),
      });
    } catch {
      toasterApi.error({
        title: tGlobalRules('clearErrorTitle'),
        description: tGlobalRules('clearErrorDescription'),
      });
    } finally {
      setIsClearingGlobalRules(false);
    }
  }, [
    selectedTarget,
    replaceMatchReplaceRulesForApplication,
    tGlobalRules,
    updateSelectedTarget,
  ]);
  const hasServerGlobalRules =
    (selectedTarget?.browserConfig?.matchReplaceRules?.length ?? 0) > 0;
  const clearGlobalRulesDisabled =
    !selectedTarget?.id || !hasServerGlobalRules || isClearingGlobalRules;
  const hasRules = matchReplaceRules.length > 0;
  const rulesAndPreviewPanel = (
    <Splitter.Root
      orientation="vertical"
      h="100%"
      w="full"
      borderWidth="0"
      gap={0}
      size={rulesPreviewLayout.controlledSizes}
      panels={toSplitterPanels(rulesPreviewLayout.panels)}
      onResize={rulesPreviewLayout.handleResize}
      onResizeEnd={rulesPreviewLayout.handleResizeEnd}
    >
      <Splitter.Panel
        id="rules-table"
        borderWidth="0"
        w="full"
        h="full"
        minH={0}
        overflow="hidden"
      >
        {hasRules ? (
          <Box
            id="match-replace-rules-table"
            h="full"
            w="full"
            minW={0}
            overflow="hidden"
          >
            <Table
              rowData={matchReplaceRules}
              columnDefs={rulesColumnDefs}
              defaultColDef={rulesDefaultColDef}
              getRowId={getRuleRowId}
              rowHeight={RULES_ROW_HEIGHT}
              headerHeight={32}
              noBorder
              fontSize={12}
              headerFontSize={11}
              animateRows={false}
              suppressCellFocus
              debounceVerticalScrollbar
              domLayout="normal"
              fitColumnsOnGridSizeChanged={false}
              hideColumnControls
              autoPinActionsColumn
              getRowStyle={getRuleRowStyle}
              onRowClicked={handleRuleRowClicked}
            />
          </Box>
        ) : (
          <Box h="full" w="full">
            <EmptyState
              icon={ReplaceIcon}
              title={tGlobalRules('emptyTitle')}
              description={tGlobalRules('emptyDescription')}
            />
          </Box>
        )}
      </Splitter.Panel>

      <BorderedSplitterResizeTrigger
        id="rules-table:preview"
        orientation="vertical"
      />

      <Splitter.Panel
        id="preview"
        borderWidth="0"
        w="full"
        h="full"
        minH={0}
        overflow="hidden"
      >
        <Splitter.Root
          size={requestsPreviewLayout.controlledSizes}
          panels={toSplitterPanels(requestsPreviewLayout.panels)}
          onResize={requestsPreviewLayout.handleResize}
          onResizeEnd={requestsPreviewLayout.handleResizeEnd}
          orientation="horizontal"
          h="100%"
          w="full"
          borderWidth="0"
          gap={0}
        >
          <Splitter.Panel id="requests" borderWidth="0" w="full" h="full">
            <Box
              h="full"
              w="full"
              display="flex"
              flexDirection="column"
              minH={0}
            >
              <Box
                px="2"
                py="1"
                borderBottomWidth="1px"
                borderColor="border.subtle"
                bg="bg.subtle"
                flexShrink={0}
              >
                <Text fontSize="xs" fontWeight="medium" color="fg.default">
                  {previewRule?.matchLocation === 'response'
                    ? tPreview('testResponses')
                    : previewRule?.matchLocation === 'both'
                      ? tPreview('testTraffic')
                      : tPreview('testRequests')}
                </Text>
              </Box>
              <Box flex="1" overflow="auto">
                {requests.length === 0 ? (
                  <Box p="2">
                    <Text fontSize="xs" color="fg.muted">
                      {tPreview('noRequestsCaptured')}
                    </Text>
                  </Box>
                ) : previewRequests.length === 0 ? (
                  <Box p="2">
                    <Text fontSize="xs" color="fg.muted">
                      {tPreview('noRequestsMatchScope')}
                    </Text>
                  </Box>
                ) : (
                  previewRequests.map((request) => (
                    <Box
                      key={request.id}
                      px="2"
                      py="1"
                      borderBottomWidth="1px"
                      borderColor="border.subtle"
                      cursor="pointer"
                      bg={
                        selectedRequestId === request.id
                          ? 'bg.muted'
                          : request.wasProxyModified
                            ? getProxyModifiedRowBackground()
                            : undefined
                      }
                      _hover={{ bg: 'bg.subtle' }}
                      onClick={() => setSelectedRequestId(request.id)}
                    >
                      <HStack gap="2" alignItems="center">
                        {request.wasProxyModified ? (
                          <Box
                            flexShrink={0}
                            w="6px"
                            h="6px"
                            borderRadius="full"
                            bg={getProxyModifiedIndicatorColor()}
                          />
                        ) : null}
                        <Badge variant="emphasis" size="sm">
                          {request.method}
                        </Badge>
                        {request.status && (
                          <Badge size="sm" variant="emphasis">
                            {request.status}
                          </Badge>
                        )}
                        <Text
                          fontSize="xs"
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                          overflow="hidden"
                          flex="1"
                        >
                          {request.url}
                        </Text>
                      </HStack>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          </Splitter.Panel>

          <BorderedSplitterResizeTrigger
            id="requests:http-preview"
            orientation="horizontal"
          />

          <Splitter.Panel
            id="http-preview"
            borderWidth="0"
            w="full"
            h="full"
            minW={0}
          >
            <Splitter.Root
              panels={toSplitterPanels(originalTransformedLayout.panels)}
              size={originalTransformedLayout.controlledSizes}
              onResize={originalTransformedLayout.handleResize}
              onResizeEnd={originalTransformedLayout.handleResizeEnd}
              orientation="horizontal"
              h="100%"
              w="full"
              borderWidth="0"
              gap={0}
            >
              <Splitter.Panel id="original" borderWidth="0" w="full" h="full">
                {selectedRequestForPreview ? (
                  <HttpEditor
                    value={
                      previewRule?.matchLocation === 'response' &&
                      formattedResponse
                        ? formattedResponse
                        : previewRule?.matchLocation === 'both' &&
                            formattedResponse
                          ? `${formattedRequest}\n\n---\n\n${formattedResponse}`
                          : formattedRequest
                    }
                    readOnly
                    showViewModeToggle
                    title={tPreview('original')}
                  />
                ) : (
                  <Box p="2">
                    <Text fontSize="xs" color="fg.muted">
                      {tPreview('selectRequestToPreview')}
                    </Text>
                  </Box>
                )}
              </Splitter.Panel>

              <BorderedSplitterResizeTrigger
                id="original:transformed"
                orientation="horizontal"
              />

              <Splitter.Panel
                id="transformed"
                borderWidth="0"
                w="full"
                h="full"
              >
                <Box
                  h="full"
                  w="full"
                  display="flex"
                  flexDirection="column"
                  minH={0}
                >
                  <HStack
                    px="2"
                    py="1"
                    borderBottomWidth="1px"
                    borderColor="border.subtle"
                    bg="bg.subtle"
                    flexShrink={0}
                  >
                    <Text fontSize="xs" fontWeight="medium" color="fg.default">
                      {tPreview('transformed')}
                    </Text>
                  </HStack>
                  <Box flex="1" overflow="hidden">
                    {selectedRequestForPreview && previewRule ? (
                      !previewRule.enabled ? (
                        <Box p="2">
                          <Text fontSize="xs" color="fg.muted">
                            {tPreview('ruleDisabled')}
                          </Text>
                        </Box>
                      ) : previewResult &&
                        previewResult.matches &&
                        previewResult.result ? (
                        <HttpEditor
                          value={previewResult.result}
                          readOnly
                          showViewModeToggle
                        />
                      ) : (
                        <Box p="2">
                          <Text fontSize="xs" color="fg.muted">
                            {tPreview('ruleDidNotMatch')}
                          </Text>
                        </Box>
                      )
                    ) : (
                      <Box p="2">
                        <Text fontSize="xs" color="fg.muted">
                          {tPreview('selectRequestToSeeTransformation')}
                        </Text>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Splitter.Panel>
            </Splitter.Root>
          </Splitter.Panel>
        </Splitter.Root>
      </Splitter.Panel>
    </Splitter.Root>
  );

  return (
    <VStack h="full" w="full" gap="0" overflow="hidden">
      {/* Single Toolbar Line */}
      <HStack
        w="full"
        p="1"
        borderBottomWidth="1px"
        borderColor="border.default"
        bg="bg.subtle"
        gap="1"
        alignItems="center"
        flexShrink={0}
      >
        {/* Info - Left Side */}
        {hasRules ? (
          <>
            <Badge variant="emphasis" size="sm">
              {matchReplaceRules.filter((r) => r.enabled).length}/
              {matchReplaceRules.length}
            </Badge>
            <Text fontSize="xs" color="fg.muted">
              {tGlobalRules('activeLabel')}
            </Text>
          </>
        ) : (
          <Text fontSize="xs" fontWeight="medium" color="fg.default">
            Match & Replace
          </Text>
        )}

        <Box flex="1" />

        {hasUnsavedRules ? (
          <HStack gap="1">
            <Badge size="sm" variant="fill">
              {tGlobalRules('unsavedRulesLabel')}
            </Badge>
            {matchReplacePersistStatus === 'saving' ? (
              <Text fontSize="xs" color="fg.muted">
                {tGlobalRules('savingRulesLabel')}
              </Text>
            ) : null}
            {matchReplacePersistStatus === 'error' &&
            matchReplacePersistError ? (
              <Tooltip title={matchReplacePersistError}>
                <Text fontSize="xs" color="fg.error">
                  {tGlobalRules('saveErrorTitle')}
                </Text>
              </Tooltip>
            ) : null}
            <Button
              size="xs"
              variant="outline"
              onClick={() => {
                void persistRulesToServerNow();
              }}
            >
              {tGlobalRules('retrySaveButton')}
            </Button>
          </HStack>
        ) : null}

        {/* Actions - Right Side */}
        {selectedCount > 0 ? (
          <MatchReplaceRulesBulkActionsBar
            selectedCount={selectedCount}
            isSubmitting={false}
            onClearSelection={clearSelection}
            onEnableSelected={handleBulkEnableSelected}
            onDisableSelected={handleBulkDisableSelected}
            onExportSelected={handleBulkExportSelected}
            onDeleteSelected={() => setIsBulkDeleteDialogOpen(true)}
          />
        ) : (
          <>
            <Tooltip
              title={tGlobalRules('removeGlobalTooltip')}
              positioning={{ strategy: 'fixed' }}
            >
              <Button
                size="xs"
                variant="ghost"
                h="6"
                minW="6"
                px="0"
                loading={isClearingGlobalRules}
                disabled={clearGlobalRulesDisabled}
                aria-label={tGlobalRules('removeGlobalButton')}
                onClick={() => setIsClearGlobalDialogOpen(true)}
              >
                <Delete02Icon size={12} />
              </Button>
            </Tooltip>
            <input
              type="file"
              accept=".json"
              style={{ display: 'none' }}
              id="import-rules-input"
              onChange={handleImport}
            />
            <Tooltip title={tGlobalRules('importRules')}>
              <IconButton
                aria-label={tGlobalRules('importRules')}
                size="xs"
                variant="ghost"
                onClick={() =>
                  document.getElementById('import-rules-input')?.click()
                }
              >
                <Upload01Icon size={12} />
              </IconButton>
            </Tooltip>
            <Tooltip title={tGlobalRules('exportRules')}>
              <IconButton
                aria-label={tGlobalRules('exportRules')}
                size="xs"
                variant="ghost"
                onClick={handleExport}
              >
                <Download01Icon size={12} />
              </IconButton>
            </Tooltip>
            <IconButton
              aria-label={tGlobalRules('addRule')}
              size="xs"
              variant={isRuleEditorOpen && !editingRule ? 'subtle' : 'ghost'}
              onClick={() => {
                if (isRuleEditorOpen && !editingRule) {
                  handleCloseRuleEditor();

                  return;
                }

                handleOpenCreateRule();
              }}
            >
              <Add01Icon size={12} />
            </IconButton>
          </>
        )}
      </HStack>

      <Box flex="1" overflow="hidden" w="full" minH={0}>
        {isRuleEditorOpen ? (
          <Splitter.Root
            key="with-editor"
            orientation="horizontal"
            h="100%"
            w="full"
            minW={0}
            borderWidth="0"
            gap={0}
            size={rulesEditorLayout.controlledSizes}
            panels={toSplitterPanels(rulesEditorLayout.panels)}
            onResize={rulesEditorLayout.handleResize}
            onResizeEnd={rulesEditorLayout.handleResizeEnd}
          >
            <Splitter.Panel
              id="main"
              borderWidth="0"
              w="full"
              h="full"
              minW={0}
              overflow="hidden"
            >
              {rulesAndPreviewPanel}
            </Splitter.Panel>

            <BorderedSplitterResizeTrigger
              id="main:rule-editor"
              orientation="horizontal"
            />

            <Splitter.Panel
              id="rule-editor"
              borderWidth="0"
              w="full"
              h="full"
              minW={0}
              overflow="hidden"
            >
              <InlineRuleEditor
                ruleForm={ruleForm}
                setRuleForm={setRuleForm}
                isEditing={!!editingRule}
                onSave={handleAddRule}
                onCancel={handleCloseRuleEditor}
              />
            </Splitter.Panel>
          </Splitter.Root>
        ) : (
          rulesAndPreviewPanel
        )}
      </Box>

      <ConfirmationDialog
        isOpen={isClearGlobalDialogOpen}
        onOpenChange={({ open }) => setIsClearGlobalDialogOpen(open)}
        onConfirm={() => void handleConfirmClearGlobalRules()}
        loading={isClearingGlobalRules}
        variant="destructive"
        title={tGlobalRules('clearConfirmTitle')}
        description={tGlobalRules('clearConfirmDescription')}
        confirmText={tCommon('confirm')}
        cancelText={tCommon('cancel')}
      />
      <ConfirmationDialog
        isOpen={isBulkDeleteDialogOpen}
        onOpenChange={({ open }) => setIsBulkDeleteDialogOpen(open)}
        onConfirm={handleBulkDeleteSelected}
        variant="destructive"
        title={tBulk('deleteConfirmTitle')}
        description={tBulk('deleteConfirmDescription', {
          count: selectedCount,
        })}
        confirmText={tCommon('delete')}
        cancelText={tCommon('cancel')}
      />
    </VStack>
  );
}
